import {
  Text,
  Box,
  Flex,
  ModalOverlay,
  Modal,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Button,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import React, { useMemo, useState } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';

import { CreditAgreementFullArtifactWipConfigType } from '../../types';
import { LiabilityAccountTable } from '../entity/liabilityAccount/LiabilityAccountTable';
import { AddIconButton } from 'packages/common-frontend/components/IconButton';
import { Select } from 'packages/common-frontend/components/Select';
import { StickyHeaderSection } from 'packages/common-frontend/components/StickyHeaderSection';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';

const BASE_PROPERTY_KEY = 'liabilityAccounts';

const accountKeyMapper = ({
  tAccount,
  counterpartySlug,
}: {
  tAccount: string;
  counterpartySlug: string;
}): string => `${tAccount}-${counterpartySlug}`;

export const LiabilityAccounts = ({
  liabilityAccounts,
  wipConfig,
}: {
  liabilityAccounts: CreditAgreement.DomainEntity.LiabilityAccountProperty[];
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<string | undefined>(
    undefined,
  );

  const flags = useFeatureFlags();

  // TODO: filter out interest from available accounts (remove loan level account duplicates), then add a single account back in
  const accountSelectionOptions = useMemo<
    {
      label: string;
      value: string;
      tAccount: string;
      counterpartySlug: string;
    }[]
  >(() => {
    if (!wipConfig) return [];

    const addedAccounts = liabilityAccounts.map(accountKeyMapper);

    const mappedTAccounts = wipConfig.tAccounts.map((account) => {
      const org = wipConfig.organizationMap[account.organizationId];
      return {
        tAccount: account.tAccount,
        counterpartySlug: org.entity.slug,
      };
    });

    const remainingAccounts = mappedTAccounts.filter((account) => {
      return (
        !addedAccounts.includes(accountKeyMapper(account)) &&
        account.tAccount.startsWith('accrued')
      );
    });
    return remainingAccounts
      .map((account) => ({
        label: `${commonUtils.toSentenceCase(
          account.tAccount.replace('accrued', ''),
        )} (${account.counterpartySlug})`,
        value: accountKeyMapper(account),
        tAccount: account.tAccount,
        counterpartySlug: account.counterpartySlug,
      }))
      .sort((a, b) => (a.label > b.label ? 1 : -1));
  }, [wipConfig, liabilityAccounts]);

  // TODO: @sachinr4 - when fully releasing:
  // 1) remove check for feature flag
  // 2) remove early return for allowAccess
  // 3) replace check for allowAccess && wipConfig with just wipConfig.isMutateAllowed(BASE_PROPERTY_KEY)
  const allowAccess =
    wipConfig &&
    wipConfig.isMutateAllowed(BASE_PROPERTY_KEY) &&
    flags.allowExpenseAuthoring;

  if (!allowAccess) {
    return null;
  }

  return (
    <Box mt="40px" id="liability-accounts-main-container">
      <StickyHeaderSection labelText="Accrued Liabilities">
        <Flex alignItems="center" mb="2">
          <Text fontWeight="bold" fontSize="20px" mr="1">
            Accounts
          </Text>

          {allowAccess && wipConfig && (
            <>
              <AddIconButton
                ariaLabel="add account"
                label="Add Account"
                onClick={() => {
                  setIsOpen(true);
                }}
              />
              <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
                <ModalOverlay />
                <ModalContent padding="20px">
                  <ModalHeader>Add Liability Account</ModalHeader>
                  <ModalCloseButton />
                  <ModalBody>
                    <Select
                      value={selectedAccount}
                      onChange={(account) => setSelectedAccount(account)}
                      options={accountSelectionOptions}
                      placeholderLabel="Select account"
                    />
                    <Flex justifyContent="center" mt="8">
                      <Button
                        onClick={() => {
                          if (selectedAccount) {
                            const accounts =
                              getIn(
                                wipConfig.formProps.values,
                                BASE_PROPERTY_KEY,
                              ) ?? [];
                            const account = accountSelectionOptions.find(
                              (acct) =>
                                accountKeyMapper(acct) === selectedAccount,
                            )!;

                            wipConfig.formProps.setFieldValue(
                              BASE_PROPERTY_KEY,
                              [
                                ...accounts,
                                {
                                  name: `${commonUtils.toSentenceCase(
                                    account.tAccount.replace('accrued', ''),
                                  )}`,
                                  type:
                                    CreditAgreement.DomainEntity.PropertyType
                                      .LiabilityAccountProperty,
                                  value: 'Description of account',
                                  tAccount: account.tAccount,
                                  counterpartySlug: account.counterpartySlug,
                                  _config_: {
                                    inputConfigurations: [],
                                  },
                                  _isWrittenIn_: true,
                                },
                              ],
                            );
                            wipConfig.saveCreditAgreementFullArtifactWip({
                              variables: {
                                creditAgreementFullArtifactWip: {
                                  id: wipConfig.formProps.values.id,
                                  creditFacilityId:
                                    wipConfig.formProps.values.creditFacilityId,
                                  creditAgreement: JSON.stringify(
                                    wipConfig.formProps.values,
                                  ),
                                },
                              },
                            });
                          }
                          setIsOpen(false);
                        }}
                        aria-label="Done"
                        isDisabled={
                          selectedAccount === undefined ||
                          accountSelectionOptions.length === 0
                        }
                      >
                        Done
                      </Button>
                    </Flex>
                  </ModalBody>
                </ModalContent>
              </Modal>
            </>
          )}
        </Flex>

        <LiabilityAccountTable
          liabilityAccounts={liabilityAccounts}
          basePropertyKey={BASE_PROPERTY_KEY}
          wipConfig={wipConfig}
        />
      </StickyHeaderSection>
    </Box>
  );
};
